/**
 * AI Recruit - Team Configuration Page
 * Available under Apply Matching > Team Configuration
 *
 * Admin:   full CRUD — create teams, assign managers, manage all members
 * Manager: view their teams, add/remove members only
 */

import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import {
  UserGroupIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  UserPlusIcon,
  UserMinusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'
import { ShieldCheckIcon } from '@heroicons/react/24/solid'

import type { RootState } from '@/stores'
import { teamsApi, usersApi } from '@/services/api'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { usePermission } from '@/hooks/usePermission'

// ─── Types ──────────────────────────────────────────────────────────────────

interface TeamMember {
  id: string
  full_name: string
  email: string
  role: string
}

interface Team {
  id: string
  name: string
  organization_id: string
  manager_id: string
  manager_name: string
  manager_email: string
  member_ids: string[]
  members: TeamMember[]
  created_by_id: string
  created_at: string
  updated_at: string
}

interface OrgUser {
  id: string
  full_name: string
  email: string
  role: string
}

// ─── Role badge ─────────────────────────────────────────────────────────────

const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const map: Record<string, string> = {
    super_admin: 'bg-purple-100 text-purple-700',
    admin: 'bg-red-100 text-red-700',
    manager: 'bg-blue-100 text-blue-700',
    senior_recruiter: 'bg-indigo-100 text-indigo-700',
    recruiter: 'bg-green-100 text-green-700',
    viewer: 'bg-gray-100 text-gray-600',
  }
  const cls = map[role] ?? 'bg-gray-100 text-gray-600'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {role.replace('_', ' ')}
    </span>
  )
}

// ─── Avatar initials ─────────────────────────────────────────────────────────

const Avatar: React.FC<{ name: string; size?: 'sm' | 'md' }> = ({ name, size = 'sm' }) => {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
  const colours = [
    'bg-violet-500', 'bg-blue-500', 'bg-teal-500',
    'bg-emerald-500', 'bg-amber-500', 'bg-rose-500',
  ]
  const colour = colours[initials.charCodeAt(0) % colours.length]
  const sz = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm'
  return (
    <div className={`${sz} ${colour} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}>
      {initials}
    </div>
  )
}

// ─── Confirm Delete Modal ────────────────────────────────────────────────────

const ConfirmModal: React.FC<{
  title: string
  message: string
  onConfirm: () => void
  onCancel: () => void
  loading?: boolean
}> = ({ title, message, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 mx-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
          <TrashIcon className="w-5 h-5 text-red-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="text-sm text-gray-600 mb-6">{message}</p>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} className="btn-outline text-sm px-4 py-2 rounded-lg">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
)

// ─── Create / Edit Team Modal ────────────────────────────────────────────────

const TeamFormModal: React.FC<{
  mode: 'create' | 'edit'
  initialName?: string
  initialManagerId?: string
  orgUsers: OrgUser[]
  onSubmit: (name: string, managerId: string) => void
  onClose: () => void
  loading?: boolean
}> = ({ mode, initialName = '', initialManagerId = '', orgUsers, onSubmit, onClose, loading }) => {
  const [name, setName] = useState(initialName)
  const [managerId, setManagerId] = useState(initialManagerId)
  const [search, setSearch] = useState('')
  const [errors, setErrors] = useState<{ name?: string; manager?: string }>({})

  const eligible = useMemo(
    () =>
      orgUsers.filter(
        (u) =>
          u.role === 'manager' &&
          (u.full_name.toLowerCase().includes(search.toLowerCase()) ||
            u.email.toLowerCase().includes(search.toLowerCase()))
      ),
    [orgUsers, search]
  )

  const selectedUser = orgUsers.find((u) => u.id === managerId)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: { name?: string; manager?: string } = {}
    if (!name.trim()) newErrors.name = 'Team name is required'
    else if (name.trim().length > 50) newErrors.name = 'Team name must be 50 characters or less'
    if (!managerId) newErrors.manager = 'Please select a manager'
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return }
    onSubmit(name.trim(), managerId)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'create' ? 'Create New Team' : 'Edit Team'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
            {/* Team Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Team Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((prev) => ({ ...prev, name: undefined })) }}
                placeholder="e.g. Engineering East, Sales Team A…"
                maxLength={50}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            {/* Manager Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Assign Manager <span className="text-red-500">*</span>
              </label>
              {errors.manager && <p className="mb-1.5 text-xs text-red-500">{errors.manager}</p>}
              {selectedUser && (
                <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
                  <Avatar name={selectedUser.full_name} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{selectedUser.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">{selectedUser.email}</p>
                  </div>
                  <RoleBadge role={selectedUser.role} />
                  <button
                    type="button"
                    onClick={() => setManagerId('')}
                    className="ml-1 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="relative mb-2">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {eligible.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-gray-500 text-center">No eligible users found</p>
                ) : (
                  eligible.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => { setManagerId(u.id); setSearch(''); setErrors((prev) => ({ ...prev, manager: undefined })) }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                        managerId === u.id ? 'bg-primary-50' : ''
                      }`}
                    >
                      <Avatar name={u.full_name} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{u.full_name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                      <RoleBadge role={u.role} />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="btn-outline text-sm px-4 py-2 rounded-lg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary text-sm px-5 py-2 rounded-lg disabled:opacity-50"
            >
              {loading
                ? mode === 'create' ? 'Creating…' : 'Saving…'
                : mode === 'create' ? 'Create Team' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Manage Members Modal ────────────────────────────────────────────────────

const ManageMembersModal: React.FC<{
  team: Team
  orgUsers: OrgUser[]
  canEdit: boolean
  onAddMember: (userId: string) => void
  onRemoveMember: (userId: string) => void
  onClose: () => void
  mutating: boolean
}> = ({ team, orgUsers, canEdit, onAddMember, onRemoveMember, onClose, mutating }) => {
  const [search, setSearch] = useState('')
  const [addSearch, setAddSearch] = useState('')

  const memberIds = new Set(team.member_ids)

  const filteredMembers = team.members.filter(
    (m) =>
      m.full_name.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase())
  )

  const addableUsers = useMemo(
    () =>
      orgUsers.filter(
        (u) =>
          !memberIds.has(u.id) &&
          u.id !== team.manager_id &&
          (u.full_name.toLowerCase().includes(addSearch.toLowerCase()) ||
            u.email.toLowerCase().includes(addSearch.toLowerCase()))
      ),
    [orgUsers, memberIds, team.manager_id, addSearch]
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{team.name}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Manager: <span className="font-medium text-gray-700">{team.manager_name}</span>
              {' · '}
              {team.members.length} {team.members.length === 1 ? 'member' : 'members'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden divide-x divide-gray-100">
          {/* Current Members */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="px-4 pt-4 pb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Current Members
              </p>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search members…"
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-1">
              {filteredMembers.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-center">
                  <UserGroupIcon className="w-8 h-8 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400">No members yet</p>
                </div>
              ) : (
                filteredMembers.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 group"
                  >
                    <Avatar name={m.full_name} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{m.full_name}</p>
                      <p className="text-xs text-gray-500 truncate">{m.email}</p>
                    </div>
                    <RoleBadge role={m.role} />
                    {canEdit && (
                      <button
                        onClick={() => onRemoveMember(m.id)}
                        disabled={mutating}
                        title="Remove member"
                        className="ml-1 p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all disabled:cursor-not-allowed"
                      >
                        <UserMinusIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Add Members — only when canEdit */}
          {canEdit && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 pt-4 pb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Add Members
                </p>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    placeholder="Search users…"
                    className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-1">
                {addableUsers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-center">
                    <p className="text-sm text-gray-400">
                      {addSearch ? 'No users match your search' : 'All users are already members'}
                    </p>
                  </div>
                ) : (
                  addableUsers.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 group"
                    >
                      <Avatar name={u.full_name} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{u.full_name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                      <RoleBadge role={u.role} />
                      <button
                        onClick={() => onAddMember(u.id)}
                        disabled={mutating}
                        title="Add to team"
                        className="ml-1 p-1 rounded text-gray-300 hover:text-primary-600 hover:bg-primary-50 opacity-0 group-hover:opacity-100 transition-all disabled:cursor-not-allowed"
                      >
                        <UserPlusIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="btn-outline text-sm px-4 py-2 rounded-lg">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Team Row ────────────────────────────────────────────────────────────────

const TeamRow: React.FC<{
  team: Team
  canManageAllTeams: boolean
  canManageOwnTeam: boolean
  bypassesOwnership: boolean
  isManagerOfTeam: boolean
  onEdit: (team: Team) => void
  onDelete: (team: Team) => void
  onManageMembers: (team: Team) => void
}> = ({ team, canManageAllTeams, canManageOwnTeam, bypassesOwnership, isManagerOfTeam, onEdit, onDelete, onManageMembers }) => {
  const canManage = bypassesOwnership || isManagerOfTeam
  const displayedMembers = team.members.slice(0, 5)
  const overflow = team.members.length - displayedMembers.length

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <UserGroupIcon className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{team.name}</p>
            <p className="text-xs text-gray-500 whitespace-nowrap">
              Created {new Date(team.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </td>
      <td className="px-5 py-4">
        <div className="flex items-center gap-2">
          <Avatar name={team.manager_name} size="sm" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate max-w-[160px]">
              {team.manager_name}
            </p>
            <p className="text-xs text-gray-500 truncate max-w-[160px]">{team.manager_email}</p>
          </div>
        </div>
      </td>
      <td className="px-5 py-4">
        <div className="flex items-center gap-1.5">
          {displayedMembers.map((m) => (
            <div key={m.id} title={m.full_name}>
              <Avatar name={m.full_name} size="sm" />
            </div>
          ))}
          {overflow > 0 && (
            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
              +{overflow}
            </div>
          )}
          {team.members.length === 0 && (
            <span className="text-xs text-gray-400 italic">No members</span>
          )}
        </div>
      </td>
      <td className="px-5 py-4">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
          {team.members.length}
        </span>
      </td>
      <td className="px-5 py-4">
        <div className="flex items-center gap-1">
          {/* Disabled (not hidden) for a manager viewing a team they don't
              own — keeps the org's team structure visible while still
              blocking the action. RBAC_UI_IMPLEMENTATION_PLAN.md §5.7. */}
          {canManageOwnTeam && (
            <button
              onClick={() => canManage && onManageMembers(team)}
              disabled={!canManage}
              title={canManage ? 'Manage members' : "You're not this team's manager"}
              className={
                canManage
                  ? 'p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors'
                  : 'p-1.5 rounded-lg text-gray-300 cursor-not-allowed'
              }
            >
              <UserGroupIcon className="w-4 h-4" />
            </button>
          )}
          {canManageAllTeams && (
            <>
              <button
                onClick={() => onEdit(team)}
                title="Edit team"
                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(team)}
                title="Delete team"
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const TeamConfigurationPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const qc = useQueryClient()

  // Rewritten 2026-08-14 (§10.1) onto the real backend capability split:
  // `teams.manageAll` (admin+, full CRUD — Create/Edit/Delete team) and
  // the ownership-filtered routes (manager+, matches today's manager
  // access exactly, hierarchically, so it's also true for admin/
  // super_admin). `bypassesOwnership` mirrors the backend's own
  // `_bypasses_ownership()` in teams.py exactly — NOT the same set as
  // `canManageAllTeams` holders in general (a custom role could hold
  // `teams.manageAll` without being an ownership bypass), so tracked
  // independently rather than assumed equal.
  //
  // `canManageOwnTeam` gates the single "Manage members" button, which
  // opens one modal covering both adding and removing (ManageMembersModal
  // has no separate add/remove gating) — split 2026-08-23 from the old
  // shared `teams.manageOwn` into `teams.addMemberOwn`/`removeMemberOwn`,
  // so this stays an OR of both rather than picking just one, preserving
  // exactly who can open that modal today.
  //
  // `canManageAllTeams` gates Create/Edit/Delete team + the stats grid,
  // all as one flag, same as before this comment was written — split
  // 2026-08-23 from the old shared `teams.manageAll` into
  // teams.create/rename/reassignManager/delete (Edit covers both rename
  // and reassign-manager in one form, matching update_team's single PUT
  // body). Kept as one combined OR here too, not four separate UI-level
  // checks: none of these four buttons had independent gating before this
  // split, and introducing that now would be new frontend behavior beyond
  // what was asked — every one of today's roles holds all four together
  // (identical min_role), so this OR is exactly equivalent to the old
  // single flag for every role that exists right now.
  const { can, bypassesOwnership } = usePermission()
  const canManageAllTeams =
    can('teams.create') || can('teams.rename') || can('teams.reassignManager') || can('teams.delete')
  const canManageOwnTeam = can('teams.addMemberOwn') || can('teams.removeMemberOwn')
  // Gate mismatch fix (Priority 4 audit) — the org-users fetch backing the
  // "add member" picker below has always been gated on users.viewRoster on
  // the backend (gated("users.viewRoster", ...), users.py's
  // get_all_recruiters), with no matching frontend check.
  const canViewRoster = can('users.viewRoster')

  // ── Modals state ──────────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Team | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Team | null>(null)
  const [membersTarget, setMembersTarget] = useState<Team | null>(null)

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: teamsData, isLoading: teamsLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.list().then((r) => r.data as { teams: Team[]; total: number }),
  })

  const { data: usersData } = useQuery({
    queryKey: ['org-users'],
    queryFn: () =>
      usersApi.getAllRecruiters().then((r) => {
        const raw = r.data
        const arr: OrgUser[] = (raw?.users ?? raw?.items ?? raw ?? []).map((u: any) => ({
          id: u.id,
          full_name: u.full_name,
          email: u.email,
          role: u.role,
        }))
        return arr
      }),
    enabled: canViewRoster,
  })

  const orgUsers: OrgUser[] = usersData ?? []
  const teams: Team[] = teamsData?.teams ?? []

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: ({ name, managerId }: { name: string; managerId: string }) =>
      teamsApi.create({ name, manager_id: managerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] })
      toast.success('Team created successfully')
      setCreateOpen(false)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Failed to create team'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, name, managerId }: { id: string; name: string; managerId: string }) =>
      teamsApi.update(id, { name, manager_id: managerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] })
      toast.success('Team updated successfully')
      setEditTarget(null)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Failed to update team'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => teamsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams'] })
      toast.success('Team deleted')
      setDeleteTarget(null)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Failed to delete team'),
  })

  const addMemberMut = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      teamsApi.addMember(teamId, userId),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['teams'] })
      // update the modal's live data
      setMembersTarget(res.data as Team)
      toast.success('Member added')
    },
    // Skips its own toast on a 403 (bug fix, 2026-08-26) — the shared apiV2
    // response interceptor (services/api.ts) already toasts every 403
    // app-wide; this handler duplicated it, showing the identical
    // "Missing permission: ..." message twice for one denied attempt.
    // Still toasts for any other error type, since the interceptor only
    // acts on 403.
    onError: (e: any) => {
      if (e?.response?.status === 403) return
      toast.error(e?.response?.data?.detail ?? 'Failed to add member')
    },
  })

  const removeMemberMut = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      teamsApi.removeMember(teamId, userId),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['teams'] })
      setMembersTarget(res.data as Team)
      toast.success('Member removed')
    },
    // Same duplicate-toast fix as addMemberMut above.
    onError: (e: any) => {
      if (e?.response?.status === 403) return
      toast.error(e?.response?.data?.detail ?? 'Failed to remove member')
    },
  })

  // ── Derived stats ─────────────────────────────────────────────────────────
  const totalMembers = teams.reduce((acc, t) => acc + t.members.length, 0)
  const uniqueManagers = new Set(teams.map((t) => t.manager_id)).size

  // ─────────────────────────────────────────────────────────────────────────

  if (teamsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Page Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1 uppercase tracking-wider font-medium">
            <span>Apply Matching</span>
            <span className="text-gray-300">/</span>
            <span className="text-primary-600">Team Configuration</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheckIcon className="w-7 h-7 text-primary-600" />
            Team Configuration
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {canManageAllTeams
              ? 'Create and manage teams, assign managers, and configure team membership.'
              : 'Manage your team members.'}
          </p>
        </div>
        {canManageAllTeams && (
          <button
            onClick={() => setCreateOpen(true)}
            className="btn-primary flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg"
          >
            <PlusIcon className="w-4 h-4" />
            Create Team
          </button>
        )}
      </div>

      {/* Stats */}
      {canManageAllTeams && (
        <div className="grid grid-cols-3 gap-4 mb-7">
          {[
            { label: 'Total Teams', value: teams.length, colour: 'text-primary-600 bg-primary-50' },
            { label: 'Total Members', value: totalMembers, colour: 'text-emerald-600 bg-emerald-50' },
            { label: 'Managers', value: uniqueManagers, colour: 'text-violet-600 bg-violet-50' },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs text-gray-500 font-medium mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.colour.split(' ')[0]}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Teams Table */}
      {teams.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <UserGroupIcon className="w-7 h-7 text-gray-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">No teams yet</h3>
          <p className="text-sm text-gray-500 max-w-xs">
            {canManageAllTeams
              ? 'Create your first team to get started organizing your recruitment workforce.'
              : 'You have not been assigned as a manager to any team yet.'}
          </p>
          {canManageAllTeams && (
            <button
              onClick={() => setCreateOpen(true)}
              className="mt-5 btn-primary flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg"
            >
              <PlusIcon className="w-4 h-4" />
              Create First Team
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr className="bg-gray-50">
                {['Team', 'Manager', 'Members', 'Count', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {teams.map((team) => (
                <TeamRow
                  key={team.id}
                  team={team}
                  canManageAllTeams={canManageAllTeams}
                  canManageOwnTeam={canManageOwnTeam}
                  bypassesOwnership={bypassesOwnership}
                  isManagerOfTeam={team.manager_id === user?.id}
                  onEdit={setEditTarget}
                  onDelete={setDeleteTarget}
                  onManageMembers={setMembersTarget}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      {createOpen && (
        <TeamFormModal
          mode="create"
          orgUsers={orgUsers}
          loading={createMut.isPending}
          onSubmit={(name, managerId) => createMut.mutate({ name, managerId })}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {editTarget && (
        <TeamFormModal
          mode="edit"
          initialName={editTarget.name}
          initialManagerId={editTarget.manager_id}
          orgUsers={orgUsers}
          loading={updateMut.isPending}
          onSubmit={(name, managerId) =>
            updateMut.mutate({ id: editTarget.id, name, managerId })
          }
          onClose={() => setEditTarget(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Team"
          message={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
          loading={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {membersTarget && (
        <ManageMembersModal
          team={membersTarget}
          orgUsers={orgUsers}
          canEdit={bypassesOwnership || membersTarget.manager_id === user?.id}
          onAddMember={(userId) =>
            addMemberMut.mutate({ teamId: membersTarget.id, userId })
          }
          onRemoveMember={(userId) =>
            removeMemberMut.mutate({ teamId: membersTarget.id, userId })
          }
          onClose={() => setMembersTarget(null)}
          mutating={addMemberMut.isPending || removeMemberMut.isPending}
        />
      )}
    </div>
  )
}

export default TeamConfigurationPage
